/**
 * Shared pricing section used by the homepage (/#pricing) and the
 * dedicated /pricing page. Self-contained: owns its own monthly/annual
 * toggle so both mount points work independently.
 *
 * "Master" is the display name for the mid tier — the underlying
 * `plan_tier` code stored in Postgres + Stripe is still `team`, so the
 * backend billing enforcement doesn't need a migration.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Lock, ArrowRight } from "lucide-react";

export const TIERS = [
  {
    key: "free", name: "Free", price: "$0", cadence: "forever",
    tagline: "Includes 1–3 Clients (CRM Only — DocuSign not included).",
    features: [
      "1–3 clients (CRM only)",
      "Full CRM + client portal",
      "Listing recommendations",
      "AI daily briefings",
    ],
    docusign: false,
    cta: "Get started free",
  },
  {
    key: "growth", name: "Growth", price: "$39.99", cadence: "per month",
    tagline: "For the solo agent building momentum — DocuSign included.",
    features: [
      "Up to 10 clients",
      "DocuSign e-signatures",
      "Client portal + AI briefings",
      "Priority email support",
    ],
    docusign: true,
    cta: "Choose Growth",
  },
  {
    // key stays "team" so the backend + Stripe don't need to know about
    // the rebrand. Only the surfaced name changed.
    key: "team", name: "Master", price: "$79.99", cadence: "per month",
    tagline: "One agent, bigger book — up to 20 clients.",
    features: [
      "Up to 20 clients",
      "DocuSign e-signatures",
      "Everything in Growth",
      "Priority email support",
    ],
    docusign: true, popular: true,
    cta: "Choose Master",
  },
  {
    key: "pro", name: "Pro", price: "$119.99", cadence: "per month",
    tagline: "Unlimited pipeline — no client cap.",
    features: [
      "Unlimited clients (21+)",
      "DocuSign e-signatures",
      "Everything in Master",
      "Priority email support",
    ],
    docusign: true,
    cta: "Choose Pro",
  },
];

export default function PricingBlock({ withHeading = true }) {
  const [annual, setAnnual] = useState(false);
  const priceOf = (t) => {
    if (t.key === "free") return { price: t.price, cadence: t.cadence };
    const monthly = parseFloat(t.price.replace(/[^0-9.]/g, ""));
    if (!annual) return { price: `$${monthly.toFixed(2)}`, cadence: "per month" };
    const annualPerMonth = monthly * 0.82;
    return { price: `$${annualPerMonth.toFixed(2)}`, cadence: "per month · billed annually" };
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {withHeading && (
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>Pricing</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">Priced by your book — not your headcount.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">Free forever for your first 3 clients. Upgrade when you're ready — every paid tier includes DocuSign.</p>
          <p className="mt-4 text-[13px] text-slate-700 font-semibold" data-testid="pricing-value-anchor">
            CRM + DocuSign + client portal + transaction tracker. <span className="text-slate-500 font-normal">One subscription. Less than the cost of DocuSign alone.</span>
          </p>
        </div>
      )}

      <div className="mt-6 flex justify-center">
        <div className="inline-flex items-center gap-1 rounded-full bg-white ring-1 ring-slate-200 p-1" data-testid="pricing-cadence-toggle">
          <button type="button" onClick={() => setAnnual(false)} className={`px-4 py-1.5 rounded-full text-xs font-bold ${!annual ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`} data-testid="pricing-monthly-toggle">Monthly</button>
          <button type="button" onClick={() => setAnnual(true)} className={`px-4 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-2 ${annual ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`} data-testid="pricing-annual-toggle">
            Annual
            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px] font-bold">Save 18%</span>
          </button>
        </div>
      </div>

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {TIERS.map(t => {
          const p = priceOf(t);
          return (
            <div key={t.key} className={`rounded-2xl p-6 ring-1 ring-slate-200 bg-white relative flex flex-col ${t.popular ? "ring-2 shadow-xl" : ""}`}
                 style={t.popular ? { ringColor: "var(--primary-hex)", boxShadow: "0 20px 45px -20px rgba(31,111,235,0.35)" } : {}}
                 data-testid={`landing-tier-${t.key}`}>
              {t.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white" style={{ background: "var(--primary-hex)" }}>Most popular</div>
              )}
              <div className="font-heading text-xl font-bold tracking-tight text-slate-900">{t.name}</div>
              <div className="mt-3 flex items-baseline gap-1.5">
                <div className="font-heading text-4xl font-bold tabular-nums text-slate-900">{p.price}</div>
                <div className="text-sm text-slate-500">{p.cadence}</div>
              </div>
              <p className="mt-2 text-sm text-slate-600 min-h-[40px]">{t.tagline}</p>
              <div className="mt-4">
                {t.docusign ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"><Check className="h-3 w-3" /> DocuSign included</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-rose-800 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full"><Lock className="h-3 w-3" /> DocuSign not included</span>
                )}
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {t.features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--primary-hex)" }} /> {f}</li>
                ))}
              </ul>
              <Link to="/auth" className={`mt-6 inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2.5 text-sm font-semibold ${t.popular ? "text-white" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                    style={t.popular ? { background: "var(--primary-hex)" } : {}}
                    data-testid={`landing-tier-${t.key}-cta`}>
                {t.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          );
        })}
      </div>

      <div className="mt-16 rounded-2xl ring-1 ring-slate-200 bg-white overflow-x-auto" data-testid="pricing-matrix">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="p-4 font-heading font-bold text-slate-700 w-1/3">What you get</th>
              <th className="p-4 font-heading font-bold text-slate-700 text-center">Free</th>
              <th className="p-4 font-heading font-bold text-slate-700 text-center">Growth</th>
              <th className="p-4 font-heading font-bold text-center" style={{ color: "var(--primary-hex)" }}>Master</th>
              <th className="p-4 font-heading font-bold text-slate-700 text-center">Pro</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {[
              { label: "Client cap",              cells: ["1–3",     "10",       "20",       "Unlimited"] },
              { label: "DocuSign e-signatures",   cells: [false,     true,       true,       true] },
              { label: "Branded client portal",   cells: [true,      true,       true,       true] },
              { label: "AI Run My Day briefing",  cells: [true,      true,       true,       true] },
              { label: "MLS-style listing pushes",cells: [true,      true,       true,       true] },
              { label: "Transaction deadlines",   cells: [true,      true,       true,       true] },
              { label: "Priority email support",  cells: [false,     true,       true,       true] },
              { label: "Included users",          cells: ["1",       "1",        "1",        "1"] },
              { label: "CSV data export",         cells: [true,      true,       true,       true] },
            ].map(r => (
              <tr key={r.label}>
                <td className="p-4 font-semibold text-slate-900">{r.label}</td>
                {r.cells.map((c, i) => (
                  <td key={i} className="p-4 text-center">
                    {c === true  ? <Check className="h-4 w-4 inline text-emerald-600" /> :
                     c === false ? <span className="text-slate-300">—</span> :
                                   <span className="text-slate-700">{c}</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-8 text-center text-xs text-slate-500">
        Running a brokerage or a multi-agent team?{" "}
        <a href="mailto:sales@ikey.app?subject=Brokerage%20pricing%20inquiry" className="font-semibold underline" style={{ color: "var(--primary-hex)" }}>Contact us for brokerage pricing →</a>
      </p>
    </div>
  );
}
